/**
 * MissingInformationSection — section partagée MI broker (Chunk 9.L.12).
 *
 * Surface UI nested sous ProjectDetailPage + SubmissionDetailPage : liste les
 * MI scopées sur le filtre fourni + actions transition (fulfill/waive/reopen/
 * supersede) gating-aware côté status.
 *
 * Backend wire shapes (cf. apps/web/src/lib/queries/missingInformation.ts) :
 *   - list : `{items: MissingInformationRow[], next_cursor: string|null}`
 *   - transitions PATCH : full row post-update (re-SELECT côté backend)
 *
 * Hooks consommés selon `scope` prop :
 *   - `project` → `useMissingInformationByProject({projectId})`
 *   - `submission` → `useMissingInformationBySubmission({submissionId})`
 *
 * Microcopy outbound hint (PO décision Q5 9.L.12) :
 *   "Informations bloquantes à résoudre avant validation de la soumission."
 *
 * Gating UX terminal : selon status, seuls certains boutons apparaissent :
 *   - open / in_progress → Résoudre + Sans objet + Remplacer
 *   - fulfilled / waived → Rouvrir + Remplacer
 *   - superseded         → aucun bouton (terminal pur)
 *
 * Anti-patterns évités :
 *   - Pas de `<div onClick>` — boutons natifs.
 *   - i18n strict (pas de FR hardcodée).
 *   - Refetch via invalidation cache des hooks de mutation (parité 9.L.8).
 */

import type { MissingInformationRow } from "@kollectivia/shared";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { AgentBadge } from "../../../components/AgentBadge";
import {
  useMissingInformationByProject,
  useMissingInformationBySubmission,
} from "../../../lib/queries/missingInformation";

import { FulfillMissingInfoDialog } from "./FulfillMissingInfoDialog";
import { unItemParExigence } from "./missingInformationCourants";
import { MissingInfoStatusBadge } from "./MissingInfoStatusBadge";
import { ReopenMissingInfoDialog } from "./ReopenMissingInfoDialog";
import { SupersedeMissingInfoDialog } from "./SupersedeMissingInfoDialog";
import { WaiveMissingInfoDialog } from "./WaiveMissingInfoDialog";

export type MissingInformationSectionProps =
  | { scope: "project"; projectId: string }
  | { scope: "submission"; submissionId: string };

/** Statuts qui acceptent fulfill + waive + supersede. */
const PENDING_STATUSES = new Set(["open", "in_progress"]);

/** Statuts qui acceptent reopen + supersede. */
const RESOLVED_STATUSES = new Set(["fulfilled", "waived"]);

/** Statut terminal sans action possible. */
const TERMINAL_STATUSES = new Set(["superseded"]);

export function MissingInformationSection(props: MissingInformationSectionProps) {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage?.startsWith("fr") ? "fr" : "en";

  const byProject = useMissingInformationByProject({
    projectId: props.scope === "project" ? props.projectId : undefined,
    enabled: props.scope === "project",
  });
  const bySubmission = useMissingInformationBySubmission({
    submissionId: props.scope === "submission" ? props.submissionId : undefined,
    enabled: props.scope === "submission",
  });
  const query = props.scope === "project" ? byProject : bySubmission;

  const [fulfillingFor, setFulfillingFor] = useState<MissingInformationRow | null>(null);
  const [waivingFor, setWaivingFor] = useState<MissingInformationRow | null>(null);
  const [reopeningFor, setReopeningFor] = useState<MissingInformationRow | null>(null);
  const [supersedingFor, setSupersedingFor] = useState<MissingInformationRow | null>(null);

  // Une exigence, une vérité. Le modèle conserve l'historique — un requis
  // satisfait puis redevenu non satisfait laisse une ligne close et une ligne
  // ouverte — mais cet écran répond à « qu'est-ce qui manque MAINTENANT ».
  const items = useMemo<MissingInformationRow[]>(
    () => unItemParExigence(query.data?.items ?? []),
    [query.data?.items],
  );

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-2 flex flex-col gap-1">
        <h2 className="text-sm font-medium uppercase tracking-wide text-slate-500">
          {t("missing_information.section.title")}
        </h2>
        <p className="mt-0.5 text-xs text-slate-500">{t("missing_information.section.hint")}</p>
      </div>

      {items.length > 0 && (
        <p className="mb-3 text-xs text-slate-500">
          {t("missing_information.section.count", { count: items.length })}
        </p>
      )}

      {query.isLoading ? (
        <p className="text-sm text-slate-400">{t("app.loading")}</p>
      ) : query.isError ? (
        <div className="flex flex-col gap-2">
          <p className="text-sm text-red-700">{t("missing_information.section.error")}</p>
          <button
            type="button"
            onClick={() => void query.refetch()}
            className="text-brand-700 focus-visible:outline-brand-500 self-start text-xs hover:underline focus-visible:outline-2 focus-visible:outline-offset-2"
          >
            {t("app.retry")}
          </button>
        </div>
      ) : items.length === 0 ? (
        <p className="text-sm text-slate-400">{t("missing_information.section.empty")}</p>
      ) : (
        <ul className="divide-y divide-slate-200">
          {items.map((mi) => (
            <MissingInfoRowItem
              key={mi.id}
              mi={mi}
              lang={lang}
              onFulfill={() => setFulfillingFor(mi)}
              onWaive={() => setWaivingFor(mi)}
              onReopen={() => setReopeningFor(mi)}
              onSupersede={() => setSupersedingFor(mi)}
            />
          ))}
        </ul>
      )}

      {fulfillingFor && (
        <FulfillMissingInfoDialog
          missingInformationId={fulfillingFor.id}
          fieldLabel={
            (lang === "fr" ? fulfillingFor.field_label_fr : fulfillingFor.field_label_en) ??
            fulfillingFor.field_path
          }
          onClose={() => setFulfillingFor(null)}
        />
      )}
      {waivingFor && (
        <WaiveMissingInfoDialog
          missingInformationId={waivingFor.id}
          fieldLabel={
            (lang === "fr" ? waivingFor.field_label_fr : waivingFor.field_label_en) ??
            waivingFor.field_path
          }
          onClose={() => setWaivingFor(null)}
        />
      )}
      {reopeningFor && (
        <ReopenMissingInfoDialog
          missingInformationId={reopeningFor.id}
          fieldLabel={
            (lang === "fr" ? reopeningFor.field_label_fr : reopeningFor.field_label_en) ??
            reopeningFor.field_path
          }
          onClose={() => setReopeningFor(null)}
        />
      )}
      {supersedingFor && (
        <SupersedeMissingInfoDialog
          missingInformationId={supersedingFor.id}
          fieldLabel={
            (lang === "fr" ? supersedingFor.field_label_fr : supersedingFor.field_label_en) ??
            supersedingFor.field_path
          }
          onClose={() => setSupersedingFor(null)}
        />
      )}
    </section>
  );
}

// -----------------------------------------------------------------------------
// MissingInfoRowItem — row enrichi avec status badge + boutons gated
// -----------------------------------------------------------------------------

type RowProps = {
  mi: MissingInformationRow;
  lang: "fr" | "en";
  onFulfill: () => void;
  onWaive: () => void;
  onReopen: () => void;
  onSupersede: () => void;
};

function MissingInfoRowItem({ mi, lang, onFulfill, onWaive, onReopen, onSupersede }: RowProps) {
  const { t } = useTranslation();

  const label =
    (lang === "fr" ? mi.field_label_fr : mi.field_label_en) ??
    (mi.field_path.startsWith("mandate.") ? t("request_flow.purpose_mandate") : mi.field_path);
  const isPending = PENDING_STATUSES.has(mi.status);
  const isResolved = RESOLVED_STATUSES.has(mi.status);
  const isTerminal = TERMINAL_STATUSES.has(mi.status);

  return (
    <li className="flex flex-col gap-2 py-3 sm:flex-row sm:items-start sm:justify-between">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className="truncate text-sm font-medium text-slate-800">{label}</span>
          <MissingInfoStatusBadge status={mi.status} />
          <AgentBadge displayName={mi.created_by_agent_display_name} variant="created" />
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-slate-500">
          {!mi.field_path.startsWith("mandate.") && (
            <span className="font-mono">{mi.field_path}</span>
          )}
          {mi.question_audience && (
            <>
              <span aria-hidden="true">·</span>
              <span>
                {t("missing_information.section.audience_label")}{" "}
                {t(`missing_information.audiences.${mi.question_audience}`)}
              </span>
            </>
          )}
          {mi.due_at && (
            <>
              <span aria-hidden="true">·</span>
              <span>
                {t("missing_information.section.due_at_label")} {mi.due_at.slice(0, 10)}
              </span>
            </>
          )}
        </div>
        {mi.status === "waived" && mi.waiver_reason && (
          <p className="mt-1 text-xs italic text-slate-500">
            <span className="font-medium not-italic">
              {t("missing_information.section.waiver_reason_label")}
            </span>{" "}
            {mi.waiver_reason}
          </p>
        )}
        {mi.status === "fulfilled" && mi.fulfilled_at && (
          <p className="mt-1 flex flex-wrap items-center gap-2 text-xs italic text-slate-500">
            <span>
              <span className="font-medium not-italic">
                {t("missing_information.section.fulfilled_at_label")}
              </span>{" "}
              {mi.fulfilled_at.slice(0, 10)}
            </span>
            <AgentBadge displayName={mi.fulfilled_by_agent_display_name} variant="fulfilled" />
          </p>
        )}
      </div>

      {!isTerminal && (
        <div className="flex flex-wrap items-center gap-1.5 sm:flex-nowrap">
          {isPending && (
            <>
              <button
                type="button"
                onClick={onFulfill}
                className="bg-brand-600 hover:bg-brand-700 focus-visible:outline-brand-500 rounded-md px-3 py-1.5 text-xs font-medium text-white shadow-sm focus-visible:outline-2 focus-visible:outline-offset-2"
              >
                {t("missing_information.section.fulfill_button")}
              </button>
              <button
                type="button"
                onClick={onWaive}
                className="rounded-md border border-red-200 bg-white px-3 py-1.5 text-xs font-medium text-red-700 shadow-sm hover:bg-red-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-500"
              >
                {t("missing_information.section.waive_button")}
              </button>
            </>
          )}
          {isResolved && (
            <button
              type="button"
              onClick={onReopen}
              className="border-brand-200 text-brand-700 hover:bg-brand-50 focus-visible:outline-brand-500 rounded-md border bg-white px-3 py-1.5 text-xs font-medium shadow-sm focus-visible:outline-2 focus-visible:outline-offset-2"
            >
              {t("missing_information.section.reopen_button")}
            </button>
          )}
          <button
            type="button"
            onClick={onSupersede}
            className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 shadow-sm hover:bg-slate-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-500"
          >
            {t("missing_information.section.supersede_button")}
          </button>
        </div>
      )}
    </li>
  );
}
